/**
 * glosses.ts — plain-English one-line captions for investor metrics.
 *
 * Each function turns the ACTUAL value into what it means for the investor,
 * in the plain-spoken voice of the app ("Loses money even fully occupied",
 * "You own it outright"). Shared by the web detail/portfolio pages and the
 * mobile screens so both say exactly the same thing.
 *
 * Two hard rules, enforced by the tile layouts they render into:
 *   1. Short — a gloss must fit two lines of small text in a 3-column tile
 *      at 360px (≈ 30–35 characters). Say one thing.
 *   2. Never echo the tile's own number. The tile already shows "120.2%";
 *      the gloss adds the interpretation ("Loses money even fully
 *      occupied"). Any number in a gloss is DERIVED (a cushion, a share,
 *      a monthly figure), never the displayed value restated.
 *
 * Pure functions — no DOM, no app state. Null inputs mean the metric is
 * uncomputable and get an honest reason, never a fabricated number.
 */

import { compactMagnitude } from './format.ts';

function usd(n: number): string {
  return `$${Math.round(Math.abs(n)).toLocaleString('en-US')}`;
}

function pct(n: number): string {
  return `${Math.round(n)}%`;
}

function compact(n: number): string {
  return `$${compactMagnitude(n, { millionDecimals: 1, thousandFloor: 10_000 })}`;
}

// ── Listing detail (Deal Analysis) ────────────────────────────────────

/** Monthly pre-tax cash flow. The tile shows the amount; say what rent it rests on. */
export function glossCashFlow(monthly: number, monthlyRent: number | null | undefined): string {
  const rent = monthlyRent != null && monthlyRent > 0 ? usd(monthlyRent) : null;
  if (Math.round(monthly) === 0) return rent ? `Rent of ${rent} just covers costs` : 'Breaks even after all costs';
  if (monthly < 0) return rent ? `Rent of ${rent} doesn't cover costs` : 'Costs exceed the rent';
  return rent ? `Clears all costs on ${rent} rent` : 'Money left after all costs';
}

/** Cap rate = NOI ÷ price or value. Read against the 7% / 5% bands. */
export function glossCapRate(capPct: number | null | undefined): string {
  if (capPct == null) return 'Needs rent to compute';
  if (capPct <= 0) return 'Costs eat all the rent';
  if (capPct >= 7) return 'Strong yield before the loan';
  if (capPct >= 5) return 'Modest yield before the loan';
  return 'Thin yield before the loan';
}

/** Cash-on-cash = annual cash flow ÷ cash invested. 10% / 6% bands. */
export function glossCashOnCash(cocPct: number | null | undefined): string {
  if (cocPct == null) return 'No cash invested to measure';
  if (cocPct < 0) return 'Losing money on your cash';
  if (cocPct === 0) return 'Your cash earns nothing yet';
  if (cocPct >= 10) return 'Strong return on your cash';
  if (cocPct >= 6) return 'Fair return on your cash';
  return 'Thin return on your cash';
}

/** DSCR = NOI ÷ debt service. The cushion (%) is derived, not the ratio itself. */
export function glossDscr(dscr: number | null | undefined, noLoan = 'No mortgage to cover'): string {
  if (dscr == null) return noLoan;
  if (dscr < 1) return 'Rent falls short of mortgage';
  if (dscr < 1.01) return 'Rent just covers the mortgage';
  return `${pct((dscr - 1) * 100)} cushion over the mortgage`;
}

/** GRM = price (or value) ÷ annual gross rent. Read against 8 / 12 bands. */
export function glossGrm(grm: number | null | undefined): string {
  if (grm == null) return 'Needs rent to compute';
  if (grm <= 8) return 'Cheap for the rent it earns';
  if (grm <= 12) return 'Fairly priced for its rent';
  return 'Pricey for the rent it earns';
}

/** Cash to close as a share of the price (the tile shows the dollars). */
export function glossCashToClose(cash: number, price: number | null | undefined): string {
  const share = price && price > 0 ? pct((cash / price) * 100) : null;
  return share ? `${share} of the price upfront; keep 6mo reserves` : 'Upfront cash; keep 6mo reserves';
}

/** Annual after-tax cash flow vs pre-tax; the tax benefit is derived. */
export function glossAfterTax(
  annualAfterTax: number,
  annualPreTax: number,
  taxBenefit: number | null | undefined,
): string {
  const benefit = taxBenefit ?? 0;
  if (!benefit) return 'No tax benefit modeled';
  if (benefit < 0) return `Taxes reduce it by ${usd(benefit)}/yr`;
  if (annualPreTax < 0 && annualAfterTax >= 0) return `${usd(benefit)}/yr tax savings turn it positive`;
  if (annualAfterTax < 0) return `Still negative after ${usd(benefit)}/yr tax savings`;
  return `Includes ${usd(benefit)}/yr in tax savings`;
}

// ── Owned property (Portfolio Key metrics) ────────────────────────────
// Formulas mirror backend/app/services/portfolio_metrics.py.

/** NOI is shown per year; the monthly figure is the derived reading. */
export function glossNoi(noiAnnual: number, monthlyRent: number | null | undefined): string {
  if (!monthlyRent) return 'Needs rent to compute';
  if (noiAnnual <= 0) return 'Costs eat all the rent';
  return `${usd(noiAnnual / 12)}/mo after operating costs`;
}

/** LTV = loan ÷ value. The owned share is the derived reading. */
export function glossLtv(ltvPct: number, loanBalance: number): string {
  if (loanBalance <= 0 || ltvPct <= 0) return 'You own it outright';
  return `You own the other ${pct(Math.max(0, 100 - ltvPct))}`;
}

export function glossLoanBalance(loanBalance: number, currentValue: number): string {
  if (loanBalance <= 0) return 'Paid off, no mortgage';
  if (currentValue <= 0) return 'Mortgage still owed';
  return `${pct((loanBalance / currentValue) * 100)} of the value still owed`;
}

/** Cash invested = down + closing + rehab, as a share of the price. */
export function glossCashInvested(cashInvested: number, purchasePrice: number | null | undefined): string {
  if (cashInvested <= 0) return 'No cash recorded in this deal';
  if (!purchasePrice) return 'Down, closing and rehab';
  return `${pct((cashInvested / purchasePrice) * 100)} of the purchase price`;
}

/** Straight-line depreciation; the building basis is the derived figure. */
export function glossDepreciation(annual: number, depreciableBasis: number): string {
  if (annual <= 0 || depreciableBasis <= 0) return 'No building basis to depreciate';
  return `${compact(depreciableBasis)} building write-off`;
}

/** Tax savings = depreciation × marginal bracket (an estimate). */
export function glossTaxSavings(savings: number, bracketPct: number, annualDepreciation: number): string {
  if (savings <= 0 || annualDepreciation <= 0) return 'Nothing to write off yet';
  return `Depreciation × your ${pct(bracketPct)} bracket`;
}

/** Total ROI in dollars (the tile shows the %); includes 3% appreciation + paydown. */
export function glossTotalRoi(roiDollars: number, cashInvested: number): string {
  if (cashInvested <= 0) return 'No cash invested to measure';
  if (roiDollars < 0) return `\u2212${usd(roiDollars)}/yr with appreciation`;
  return `${usd(roiDollars)}/yr with appreciation`;
}

/**
 * Break-even occupancy = (operating costs + mortgage) ÷ scheduled rent.
 * Above 100% the property loses money even when fully occupied; below,
 * the vacancy cushion is the derived reading.
 */
export function glossBreakeven(breakevenPct: number, monthlyRent: number | null | undefined): string {
  if (!monthlyRent) return 'Needs rent to compute';
  if (breakevenPct > 100) return 'Loses money even fully occupied';
  if (breakevenPct >= 99.5) return 'No vacancy cushion at all';
  return `${pct(100 - breakevenPct)} vacancy cushion`;
}
